export const TERRAIN_IDS = [
  'deep-water',
  'shallow-water',
  'sand',
  'grass',
  'meadow',
  'earth',
  'mud',
  'rock',
] as const;

export type TerrainId = typeof TERRAIN_IDS[number];
export type PathId = 'trail' | 'earth-road' | 'stone-road';
export type ToolId = 'select' | 'terrain' | 'elevation-up' | 'elevation-down' | 'path' | 'object' | 'erase' | 'fill';

export interface Cell {
  terrain: TerrainId;
  elevation: number;
  variant: number;
  path: PathId | null;
}

export interface Footprint {
  width: number;
  height: number;
}

export interface ShadowConfig {
  shape: 'ellipse';
  opacity: number;
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface AssetDefinition {
  uuid: string;
  version: number;
  slug: string;
  name: string;
  type: 'building' | 'vegetation' | 'prop' | 'character' | 'creature' | 'vehicle';
  tags: string[];
  url: string;
  thumbnailUrl?: string;
  footprint: Footprint;
  pivot: { x: number; y: number };
  sortAnchor: number;
  walkable: boolean;
  highlightable: boolean;
  heightClass: 'small' | 'medium' | 'tall' | 'landmark';
  shadow: ShadowConfig;
  animations: Record<string, unknown>;
}

export interface PlacedObject {
  id: string;
  assetId: string;
  assetVersion: number;
  x: number;
  y: number;
  z: number;
  footprint: Footprint;
  state: Record<string, unknown>;
}

export interface GeneratorTargets extends Record<TerrainId, number> {}

export interface GeneratorOptions {
  seed: string;
  targets: GeneratorTargets;
  heightIntensity: number;
  landform: 'island' | 'continent';
}

export interface MapDocument {
  schemaVersion: 1;
  id: string;
  title: string;
  status: 'private' | 'publish';
  ownerId?: number;
  width: number;
  height: number;
  seed: string;
  stylePackVersion: number;
  revision: number;
  generator: GeneratorOptions;
  cells: Cell[];
  objects: PlacedObject[];
  spawnPoints: Array<{ id: string; x: number; y: number; z: number }>;
  metadata: Record<string, unknown>;
}

export interface TerrainVariant {
  attachmentId: number;
  version: number;
  url: string;
}

export interface TerrainDefinition {
  id: TerrainId;
  label: string;
  color: string;
  walkable: boolean;
  variants: TerrainVariant[];
}

export interface PathDefinition {
  id: PathId;
  label: string;
  color: string;
  url: string;
  version: number;
}

export interface AssetManifest {
  stylePackVersion: number;
  tileWidth: number;
  tileHeight: number;
  terrain: Record<TerrainId, TerrainDefinition>;
  paths: Record<PathId, PathDefinition>;
  transitions: Record<string, PathDefinition>;
  objects: AssetDefinition[];
}

export interface RuntimeConfig {
  schemaVersion: 1;
  maxMapSize: number;
  maxMapsPerUser: number;
  isLoggedIn: boolean;
  currentUserId: number;
  loginUrl: string;
  locale: string;
}

export interface BootConfig {
  restRoot: string;
  nonce: string;
  pluginUrl: string;
  isLoggedIn: boolean;
  currentUserId: number;
}

export interface MapSummary {
  id: string;
  title: string;
  status: 'private' | 'publish';
  width: number;
  height: number;
  revision: number;
  modified: string;
}

export interface HighlightOptions {
  style?: 'outline' | 'glow' | 'pulse' | 'shine';
  color?: number | string;
  intensity?: number;
  duration?: number;
}

export interface MoveOptions {
  duration?: number;
  easing?: 'linear' | 'ease-in-out';
}

export const DEFAULT_TARGETS: GeneratorTargets = {
  'deep-water': 18,
  'shallow-water': 7,
  sand: 5,
  grass: 25,
  meadow: 20,
  earth: 10,
  mud: 5,
  rock: 10,
};
